<ng-template #outcomeTileTpl let-item let-selected="selected">
  <app-icon [name]="item.value === 'no_answer' ? 'door-closed' : 'message-square'" [size]="24" />
  <span class="tile-label">{{ 'interactions.record.outcome.' + item.value | translate }}</span>
</ng-template>

<ng-template #objectionChipTpl let-item let-selected="selected">
  <span>{{ item.label }}</span>
</ng-template>

<div class="interaction" [class.notes-inline]="notesInline()" data-testid="interaction-sheet">
  @if (addressLoading() || loading()) {
    <div class="loading-container flex flex-column flex-align-center p-lg">
      <app-spinner />
    </div>
  } @else {
    @let a = address();

    @if (error()) {
      <div class="error-container p-md" data-testid="interaction-error">
        <p>{{ error() }}</p>
      </div>
    }

    @if (saved()) {
      <div class="saved-state flex flex-column flex-align-center gap-sm" data-testid="interaction-saved-state">
        <div class="saved-check">
          <app-icon name="check" [size]="20" />
        </div>
        <div class="saved-title">{{ 'interactions.record.saved.title' | translate }}</div>
        @if (a) {
          <div class="saved-address">{{ a.formatted_address }}</div>
        }
      </div>
    } @else if (dncFlaggedClosing()) {
      <div class="saved-state flex flex-column flex-align-center gap-sm" data-testid="interaction-dnc-flagged-state">
        <div class="saved-check">
          <app-icon name="ban" [size]="20" />
        </div>
        <div class="saved-title">{{ 'interactions.record.dncFlagged.title' | translate }}</div>
        @if (a) {
          <div class="saved-address">{{ a.formatted_address }}</div>
        }
      </div>
    } @else if (legacyOutcome()) {
      <div class="legacy-outcome">
        <div class="legacy-title">{{ 'interactions.record.legacyOutcome.title' | translate }}</div>
        <div class="legacy-badge">{{ 'outcomes.' + legacyOutcome() | translate }}</div>
        <form [formGroup]="legacyForm">
          <formly-form [form]="legacyForm" [fields]="legacyFields" [model]="legacyModel" />
        </form>
        <app-button variant="filled" [disabled]="saving()" (click)="saveLegacy()" data-testid="interaction-save-button">
          {{ 'interactions.record.save' | translate }}
        </app-button>
      </div>
    } @else if (isTiles()) {
      <div class="tiles-header">
        <div class="address-info">
          @if (a) {
            <div class="address-title" data-testid="interaction-address">{{ a.formatted_address }}</div>
          }
          <div class="address-subtitle">{{ 'interactions.record.subtitle' | translate }}</div>
        </div>
        <div class="overflow-wrapper" *hasPermission="'address.update'">
          <button
            type="button"
            class="overflow-trigger"
            (click)="toggleMenu()"
            [attr.aria-label]="'interactions.record.overflowMenu.label' | translate"
            data-testid="interaction-overflow-menu"
          >
            <app-icon name="ellipsis-vertical" [size]="16" />
          </button>
          @if (showMenu()) {
            <div class="overflow-menu" (clickOutside)="closeMenu()">
              <button type="button" class="overflow-menu-item" (click)="editAddress()" data-testid="interaction-edit-address">
                <app-icon name="pencil" [size]="14" />
                {{ 'interactions.record.overflowMenu.editAddress' | translate }}
              </button>
            </div>
          }
        </div>
      </div>

      <div class="dnc-row" *hasPermission="'address.manage-dnc'">
        <form [formGroup]="dncForm">
          <formly-form [form]="dncForm" [fields]="dncFields" [model]="dncModel" />
        </form>
      </div>

      <form [formGroup]="tileForm">
        <formly-form [form]="tileForm" [fields]="tileFields" [model]="tileModel" />
      </form>

      <button type="button" class="lead-button" (click)="goToLeadWizard()" data-testid="interaction-lead-button">
        <app-icon name="star" [size]="20" />
        {{ 'interactions.record.leadButton' | translate }}
      </button>
    } @else if (isSubview()) {
      <div class="subview-header">
        <button type="button" class="back-button" (click)="back()" data-testid="interaction-back-button">
          <app-icon name="chevron-left" [size]="16" />
        </button>
        <div class="subview-title-block">
          <div class="subview-title">{{ 'interactions.record.outcome.' + currentOutcome() | translate }}</div>
          @if (a) {
            <div class="address-subtitle">{{ a.formatted_address }}</div>
          }
        </div>
        @if (a?.do_not_contact) {
          <div class="dnc-badge" data-testid="interaction-dnc-badge">DNC</div>
        }
      </div>

      <div class="subview-content" data-testid="interaction-subview-content">
        <div class="subview-track" [class.subview-track--note]="noteViewOpen()">
          <div class="subview-pane">
            <form [formGroup]="detailForm">
              <formly-form [form]="detailForm" [fields]="detailFields" [model]="detailModel" />
            </form>
          </div>
          <div class="subview-pane">
            <form [formGroup]="noteForm">
              <formly-form [form]="noteForm" [fields]="noteFields" [model]="noteModel" />
            </form>
          </div>
        </div>
      </div>

      <div class="subview-footer">
        <button type="button" class="note-trigger" (click)="toggleNoteView()" data-testid="interaction-note-trigger">
          @if (noteViewOpen()) {
            <app-icon name="chevron-left" [size]="16" />
            <span>{{ 'interactions.record.back' | translate }}</span>
          } @else {
            <app-icon [name]="noteModel.notes ? 'file-text' : 'square-pen'" [size]="16" />
            @if (noteModel.notes) {
              <span class="note-preview">{{ noteModel.notes }}</span>
            } @else {
              <span>{{ 'interactions.record.addNote' | translate }}</span>
            }
          }
        </button>
        <app-button variant="filled" [disabled]="!canSave() || saving()" (click)="save()" data-testid="interaction-save-button">
          {{ 'interactions.record.save' | translate }}
        </app-button>
      </div>
    }
  }
</div>

@if (pendingDncConfirm()) {
  <app-dnc-confirm-dialog [address]="address()" (confirmed)="confirmDncGate()" (cancelled)="cancelDncGate()" />
}
